import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../App';
import {
  Shield, FileText, Eye, EyeOff, Plus, RefreshCw, ChevronRight,
  Clock, CheckCircle, XCircle, AlertTriangle, Search, X, ArrowLeft,
  BarChart3, Users, Timer, TrendingUp
} from 'lucide-react';

const SUB_TABS = [
  { id: 'dsr', label: 'DSR Cases', icon: FileText },
  { id: 'restrictions', label: 'Processing Restrictions', icon: Shield },
  { id: 'ccpa', label: 'CCPA Management', icon: Eye },
];

const DSR_STATUS_STYLES = {
  pending: { bg: 'bg-yellow-900/30', text: 'text-yellow-400', border: 'border-yellow-700' },
  in_progress: { bg: 'bg-blue-900/30', text: 'text-[#00D4FF]', border: 'border-blue-700' },
  completed: { bg: 'bg-green-900/30', text: 'text-green-400', border: 'border-green-700' },
  rejected: { bg: 'bg-red-900/30', text: 'text-[#A61E17]', border: 'border-red-700' },
};

const REQUEST_TYPES = [
  { value: 'access', label: 'Access (Right to Know)' },
  { value: 'deletion', label: 'Deletion (Right to Delete)' },
  { value: 'rectification', label: 'Rectification' },
  { value: 'portability', label: 'Data Portability' },
  { value: 'restrict', label: 'Restrict Processing' },
  { value: 'opt_out', label: 'Opt-Out of Sale' },
];

function StatusBadge({ status }) {
  const style = DSR_STATUS_STYLES[status] || DSR_STATUS_STYLES.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-wider ${style.bg} ${style.text} border ${style.border}`}>
      {status?.replace('_', ' ')}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, color = '#FF4D00' }) {
  return (
    <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color }} />
        <span className="font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0]">{label}</span>
      </div>
      <p className="text-2xl font-bold text-[#F5F1E8] font-['Barlow_Condensed']">{value}</p>
    </div>
  );
}

// ─── DSR Cases Sub-Tab ───────────────────────────────────────────────

function DSRCasesView({ workspaceId }) {
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ workspace_id: workspaceId, page, per_page: 20 });
      if (statusFilter) params.set('status', statusFilter);
      if (typeFilter) params.set('type', typeFilter);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/enterprise/dsr/cases?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCases(await res.json());
    } catch (err) {
      console.error('Failed to fetch DSR cases:', err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, statusFilter, typeFilter, page]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/enterprise/dsr/dashboard?workspace_id=${workspaceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStats(await res.json());
    } catch (err) {
      console.error('Failed to fetch DSR stats:', err);
    }
  }, [workspaceId]);

  useEffect(() => { fetchCases(); }, [fetchCases]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (selectedCase) {
    return <CaseDetailView caseId={selectedCase} onBack={() => { setSelectedCase(null); fetchCases(); }} workspaceId={workspaceId} />;
  }

  if (showCreateForm) {
    return <CreateCaseForm workspaceId={workspaceId} onClose={() => { setShowCreateForm(false); fetchCases(); fetchStats(); }} />;
  }

  return (
    <div className="space-y-4">
      {/* Dashboard Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={BarChart3} label="Total Cases" value={stats.total_cases ?? 0} color="#00D4FF" />
          <StatCard icon={Users} label="Open" value={stats.open_cases ?? 0} color="#FF4D00" />
          <StatCard icon={AlertTriangle} label="Overdue" value={stats.overdue_cases ?? 0} color="#A61E17" />
          <StatCard icon={Timer} label="Avg Resolution" value={stats.avg_resolution_time ? `${stats.avg_resolution_time}d` : 'N/A'} color="#F2A900" />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setShowCreateForm(true)} className="flex items-center gap-1.5 bg-[#FF4D00] text-[#0A0A0A] font-bold text-sm rounded px-3 py-2 hover:bg-[#E63E00] transition-colors">
          <Plus size={14} /> New Case
        </button>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] outline-none">
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
        </select>
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} className="bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] outline-none">
          <option value="">All Types</option>
          {REQUEST_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <button onClick={() => { fetchCases(); fetchStats(); }} className="p-2 text-[#A0A0A0] hover:text-[#F5F1E8] transition-colors" title="Refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Cases Table */}
      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#2A2A2A]">
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0]">Case ID</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0]">Subject</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0]">Type</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0]">Status</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0]">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#A0A0A0]">Loading...</td></tr>
            ) : cases.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#A0A0A0]">No DSR cases found</td></tr>
            ) : (
              (Array.isArray(cases) ? cases : []).map(c => (
                <tr key={c.id} className="border-b border-[#2A2A2A]/50 hover:bg-[#1E1E1E] cursor-pointer transition-colors" onClick={() => setSelectedCase(c.id)}>
                  <td className="px-4 py-3 font-mono text-[#00D4FF] text-xs">{c.id?.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-[#F5F1E8]">{c.subject_email}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#A0A0A0]">{c.request_type}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-[#A0A0A0]">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</td>
                  <td className="px-4 py-3 text-[#A0A0A0]"><ChevronRight size={14} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {cases.length > 0 && (
        <div className="flex items-center gap-2 justify-end">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 text-sm font-mono text-[#A0A0A0] border border-[#2A2A2A] rounded disabled:opacity-30 hover:border-[#FF4D00] transition-colors">Prev</button>
          <span className="font-mono text-xs text-[#A0A0A0]">Page {page}</span>
          <button onClick={() => setPage(p => p + 1)} className="px-3 py-1 text-sm font-mono text-[#A0A0A0] border border-[#2A2A2A] rounded hover:border-[#FF4D00] transition-colors">Next</button>
        </div>
      )}
    </div>
  );
}

// ─── Create Case Form ────────────────────────────────────────────────

function CreateCaseForm({ workspaceId, onClose }) {
  const [form, setForm] = useState({ subject_email: '', request_type: 'access', details: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/enterprise/dsr/cases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ workspace_id: workspaceId, ...form }),
      });
      if (res.ok) {
        onClose();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Failed to create case');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] outline-none";

  return (
    <div className="space-y-4">
      <button onClick={onClose} className="flex items-center gap-1 text-sm text-[#A0A0A0] hover:text-[#F5F1E8] transition-colors">
        <ArrowLeft size={14} /> Back to cases
      </button>
      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-6 max-w-lg">
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-[#F5F1E8] mb-4">New DSR Case</h3>
        {error && <div className="mb-4 p-3 bg-red-900/20 border border-[#A61E17] rounded text-sm text-red-400">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">Subject Email</label>
            <input type="email" required value={form.subject_email} onChange={e => setForm(f => ({ ...f, subject_email: e.target.value }))} className={inputClass} placeholder="user@example.com" />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">Request Type</label>
            <select value={form.request_type} onChange={e => setForm(f => ({ ...f, request_type: e.target.value }))} className={inputClass}>
              {REQUEST_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">Details</label>
            <textarea rows={4} value={form.details} onChange={e => setForm(f => ({ ...f, details: e.target.value }))} className={inputClass} placeholder="Describe the request..." />
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={submitting} className="bg-[#FF4D00] text-[#0A0A0A] font-bold text-sm rounded px-4 py-2 hover:bg-[#E63E00] disabled:opacity-50 transition-colors">
              {submitting ? 'Creating...' : 'Create Case'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#A0A0A0] border border-[#2A2A2A] rounded hover:border-[#FF4D00] transition-colors">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Case Detail View ────────────────────────────────────────────────

function CaseDetailView({ caseId, onBack, workspaceId }) {
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updateForm, setUpdateForm] = useState({ status: '', notes: '', resolution: '' });
  const [updating, setUpdating] = useState(false);

  const fetchCase = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/enterprise/dsr/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCaseData(data);
        setUpdateForm({ status: data.status || '', notes: '', resolution: data.resolution || '' });
      }
    } catch (err) {
      console.error('Failed to fetch case:', err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { fetchCase(); }, [fetchCase]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const body = {};
      if (updateForm.status) body.status = updateForm.status;
      if (updateForm.notes) body.notes = updateForm.notes;
      if (updateForm.resolution) body.resolution = updateForm.resolution;
      const res = await fetch(`${API}/api/enterprise/dsr/cases/${caseId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setUpdateForm(f => ({ ...f, notes: '' }));
        fetchCase();
      }
    } catch (err) {
      console.error('Failed to update case:', err);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <div className="text-[#A0A0A0] text-center py-8">Loading case...</div>;
  if (!caseData) return <div className="text-[#A0A0A0] text-center py-8">Case not found</div>;

  const inputClass = "w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] outline-none";

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-[#A0A0A0] hover:text-[#F5F1E8] transition-colors">
        <ArrowLeft size={14} /> Back to cases
      </button>

      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-[#F5F1E8]">Case {caseData.id?.slice(0, 8)}</h3>
            <p className="font-mono text-xs text-[#A0A0A0] mt-1">{caseData.subject_email}</p>
          </div>
          <StatusBadge status={caseData.status} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0]">Type</span>
            <p className="text-sm text-[#F5F1E8] font-mono mt-1">{caseData.request_type}</p>
          </div>
          <div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0]">Created</span>
            <p className="text-sm text-[#F5F1E8] font-mono mt-1">{caseData.created_at ? new Date(caseData.created_at).toLocaleString() : 'N/A'}</p>
          </div>
          <div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0]">Updated</span>
            <p className="text-sm text-[#F5F1E8] font-mono mt-1">{caseData.updated_at ? new Date(caseData.updated_at).toLocaleString() : 'N/A'}</p>
          </div>
          <div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0]">Workspace</span>
            <p className="text-sm text-[#00D4FF] font-mono mt-1">{caseData.workspace_id?.slice(0, 8)}</p>
          </div>
        </div>

        {caseData.details && (
          <div className="mb-6">
            <span className="font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0]">Details</span>
            <p className="text-sm text-[#F5F1E8] mt-1 whitespace-pre-wrap">{caseData.details}</p>
          </div>
        )}

        {/* Activity Log */}
        {caseData.activity_log && caseData.activity_log.length > 0 && (
          <div className="mb-6">
            <span className="font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2 block">Activity Log</span>
            <div className="space-y-2">
              {caseData.activity_log.map((entry, i) => (
                <div key={i} className="flex items-start gap-3 p-2 bg-[#0F0F0F] rounded border border-[#2A2A2A]/50">
                  <Clock size={12} className="text-[#A0A0A0] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-[#F5F1E8]">{entry.action || entry.notes || entry.message}</p>
                    <p className="font-mono text-[10px] text-[#A0A0A0] mt-0.5">{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''} {entry.actor ? `by ${entry.actor}` : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Update Form */}
        <form onSubmit={handleUpdate} className="border-t border-[#2A2A2A] pt-4 space-y-3">
          <span className="font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0]">Update Case</span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block font-mono text-[10px] text-[#A0A0A0] mb-1">Status</label>
              <select value={updateForm.status} onChange={e => setUpdateForm(f => ({ ...f, status: e.target.value }))} className={inputClass}>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div>
              <label className="block font-mono text-[10px] text-[#A0A0A0] mb-1">Resolution</label>
              <input value={updateForm.resolution} onChange={e => setUpdateForm(f => ({ ...f, resolution: e.target.value }))} className={inputClass} placeholder="Resolution summary..." />
            </div>
          </div>
          <div>
            <label className="block font-mono text-[10px] text-[#A0A0A0] mb-1">Notes</label>
            <textarea rows={2} value={updateForm.notes} onChange={e => setUpdateForm(f => ({ ...f, notes: e.target.value }))} className={inputClass} placeholder="Add a note..." />
          </div>
          <button type="submit" disabled={updating} className="bg-[#FF4D00] text-[#0A0A0A] font-bold text-sm rounded px-4 py-2 hover:bg-[#E63E00] disabled:opacity-50 transition-colors">
            {updating ? 'Updating...' : 'Update Case'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Processing Restrictions Sub-Tab ─────────────────────────────────

function ProcessingRestrictionsView({ workspaceId }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');

  const checkStatus = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/privacy/restriction-status?email=${encodeURIComponent(email)}&workspace_id=${workspaceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setStatus(await res.json());
      } else {
        setStatus(null);
        setMessage('User not found or error checking status');
      }
    } catch (err) {
      setMessage('Network error');
    } finally {
      setLoading(false);
    }
  }, [email, workspaceId]);

  const toggleRestriction = async (restrict) => {
    setActionLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const endpoint = restrict ? '/api/privacy/restrict-processing' : '/api/privacy/lift-restriction';
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, workspace_id: workspaceId }),
      });
      if (res.ok) {
        setMessage(restrict ? 'Processing restricted' : 'Restriction lifted');
        checkStatus();
      } else {
        const data = await res.json().catch(() => ({}));
        setMessage(data.detail || 'Operation failed');
      }
    } catch (err) {
      setMessage('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  const inputClass = "w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] outline-none";

  return (
    <div className="space-y-4">
      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-6 max-w-lg">
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-[#F5F1E8] mb-1">Processing Restrictions</h3>
        <p className="text-xs text-[#A0A0A0] mb-4">GDPR Article 18 - Restrict processing for specific users</p>

        <div className="flex gap-2 mb-4">
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} placeholder="user@example.com" onKeyDown={e => e.key === 'Enter' && checkStatus()} />
          <button onClick={checkStatus} disabled={loading || !email} className="shrink-0 bg-[#FF4D00] text-[#0A0A0A] font-bold text-sm rounded px-4 py-2 hover:bg-[#E63E00] disabled:opacity-50 transition-colors">
            {loading ? '...' : <Search size={14} />}
          </button>
        </div>

        {message && <div className="mb-4 p-3 bg-[#0F0F0F] border border-[#2A2A2A] rounded text-sm text-[#F2A900] font-mono">{message}</div>}

        {status && (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-[#0F0F0F] border border-[#2A2A2A] rounded">
              <div>
                <p className="text-sm text-[#F5F1E8]">{status.email || email}</p>
                <p className="font-mono text-[10px] text-[#A0A0A0] mt-0.5">
                  {status.restricted ? 'Processing is RESTRICTED' : 'Processing is ACTIVE'}
                </p>
              </div>
              <div className={`w-3 h-3 rounded-full ${status.restricted ? 'bg-[#A61E17]' : 'bg-green-500'}`} />
            </div>

            {status.restricted_at && (
              <p className="font-mono text-[10px] text-[#A0A0A0]">Restricted since: {new Date(status.restricted_at).toLocaleString()}</p>
            )}

            <button
              onClick={() => toggleRestriction(!status.restricted)}
              disabled={actionLoading}
              className={`w-full flex items-center justify-center gap-2 font-bold text-sm rounded px-4 py-2 transition-colors disabled:opacity-50 ${
                status.restricted
                  ? 'bg-green-700 text-white hover:bg-green-600'
                  : 'bg-[#A61E17] text-white hover:bg-red-700'
              }`}
            >
              {status.restricted ? <><Eye size={14} /> Lift Restriction</> : <><EyeOff size={14} /> Restrict Processing</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CCPA Management Sub-Tab ─────────────────────────────────────────

function CCPAManagementView({ workspaceId }) {
  const [email, setEmail] = useState('');
  const [ccpaStatus, setCcpaStatus] = useState(null);
  const [rtkData, setRtkData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showRtkData, setShowRtkData] = useState(false);

  const checkStatus = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setMessage('');
    setRtkData(null);
    setShowRtkData(false);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/privacy/ccpa/status?email=${encodeURIComponent(email)}&workspace_id=${workspaceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setCcpaStatus(await res.json());
      } else {
        setCcpaStatus(null);
        setMessage('User not found or error checking status');
      }
    } catch (err) {
      setMessage('Network error');
    } finally {
      setLoading(false);
    }
  }, [email, workspaceId]);

  const fetchRightToKnow = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/privacy/ccpa/right-to-know?email=${encodeURIComponent(email)}&workspace_id=${workspaceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setRtkData(await res.json());
        setShowRtkData(true);
      } else {
        setMessage('Failed to fetch data');
      }
    } catch (err) {
      setMessage('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOptOut = async () => {
    setActionLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/privacy/ccpa/opt-out`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, workspace_id: workspaceId }),
      });
      if (res.ok) {
        setMessage('Opt-out recorded');
        checkStatus();
      } else {
        const data = await res.json().catch(() => ({}));
        setMessage(data.detail || 'Opt-out failed');
      }
    } catch (err) {
      setMessage('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOptIn = async () => {
    setActionLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/privacy/ccpa/opt-out`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, workspace_id: workspaceId }),
      });
      if (res.ok) {
        setMessage('Opt-out cancelled');
        checkStatus();
      } else {
        const data = await res.json().catch(() => ({}));
        setMessage(data.detail || 'Opt-in failed');
      }
    } catch (err) {
      setMessage('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  const inputClass = "w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] outline-none";

  return (
    <div className="space-y-4">
      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-6 max-w-2xl">
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-[#F5F1E8] mb-1">CCPA Management</h3>
        <p className="text-xs text-[#A0A0A0] mb-4">California Consumer Privacy Act - Right to Know, Opt-Out of Sale</p>

        <div className="flex gap-2 mb-4">
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} placeholder="user@example.com" onKeyDown={e => e.key === 'Enter' && checkStatus()} />
          <button onClick={checkStatus} disabled={loading || !email} className="shrink-0 bg-[#FF4D00] text-[#0A0A0A] font-bold text-sm rounded px-4 py-2 hover:bg-[#E63E00] disabled:opacity-50 transition-colors">
            {loading ? '...' : <Search size={14} />}
          </button>
        </div>

        {message && <div className="mb-4 p-3 bg-[#0F0F0F] border border-[#2A2A2A] rounded text-sm text-[#F2A900] font-mono">{message}</div>}

        {ccpaStatus && (
          <div className="space-y-4">
            {/* Status Summary */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-[#0F0F0F] border border-[#2A2A2A] rounded">
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0]">Opt-Out Status</span>
                <div className="flex items-center gap-2 mt-1">
                  {ccpaStatus.opted_out ? (
                    <><EyeOff size={14} className="text-[#A61E17]" /><span className="text-sm text-[#A61E17] font-bold">Opted Out</span></>
                  ) : (
                    <><Eye size={14} className="text-green-400" /><span className="text-sm text-green-400 font-bold">Opted In</span></>
                  )}
                </div>
              </div>
              <div className="p-3 bg-[#0F0F0F] border border-[#2A2A2A] rounded">
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#A0A0A0]">Last Updated</span>
                <p className="text-sm text-[#F5F1E8] font-mono mt-1">{ccpaStatus.updated_at ? new Date(ccpaStatus.updated_at).toLocaleDateString() : 'N/A'}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <button onClick={fetchRightToKnow} disabled={actionLoading} className="flex items-center gap-1.5 bg-[#00D4FF] text-[#0A0A0A] font-bold text-sm rounded px-3 py-2 hover:bg-[#00B8E0] disabled:opacity-50 transition-colors">
                <FileText size={14} /> Right to Know
              </button>
              {ccpaStatus.opted_out ? (
                <button onClick={handleOptIn} disabled={actionLoading} className="flex items-center gap-1.5 bg-green-700 text-white font-bold text-sm rounded px-3 py-2 hover:bg-green-600 disabled:opacity-50 transition-colors">
                  <Eye size={14} /> Cancel Opt-Out
                </button>
              ) : (
                <button onClick={handleOptOut} disabled={actionLoading} className="flex items-center gap-1.5 bg-[#A61E17] text-white font-bold text-sm rounded px-3 py-2 hover:bg-red-700 disabled:opacity-50 transition-colors">
                  <EyeOff size={14} /> Opt-Out of Sale
                </button>
              )}
            </div>

            {/* Right to Know Data */}
            {showRtkData && rtkData && (
              <div className="border border-[#2A2A2A] rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 bg-[#1E1E1E] border-b border-[#2A2A2A]">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[#00D4FF]">Right to Know Data</span>
                  <button onClick={() => setShowRtkData(false)} className="text-[#A0A0A0] hover:text-[#F5F1E8]"><X size={12} /></button>
                </div>
                <pre className="p-4 bg-[#0F0F0F] text-xs text-[#F5F1E8] font-mono overflow-auto max-h-64 whitespace-pre-wrap">
                  {JSON.stringify(rtkData, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────

export default function PrivacyComplianceSection({ workspaceId }) {
  const [activeTab, setActiveTab] = useState('dsr');

  return (
    <div className="space-y-4">
      {/* Sub-Tab Navigation */}
      <div className="flex gap-1 border-b border-[#2A2A2A] pb-px">
        {SUB_TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-mono rounded-t transition-colors ${
                active
                  ? 'bg-[#1A1A1A] text-[#FF4D00] border border-[#2A2A2A] border-b-[#1A1A1A] -mb-px'
                  : 'text-[#A0A0A0] hover:text-[#F5F1E8]'
              }`}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'dsr' && <DSRCasesView workspaceId={workspaceId} />}
      {activeTab === 'restrictions' && <ProcessingRestrictionsView workspaceId={workspaceId} />}
      {activeTab === 'ccpa' && <CCPAManagementView workspaceId={workspaceId} />}
    </div>
  );
}
